import { memo, useEffect, useMemo, useRef, useState } from "react";
import { AspectRatio, Box, Center, Image, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { useStoreProduct } from "contexts/store_product";
import { useApp } from "contexts/app";
import { useNavigate } from "react-router";

const INITIAL_RENDER_COUNT = 30;
const RENDER_STEP = 30;

const GalleryPage = () => {
  const { products = [] } = useStoreProduct();
  const { setPageTitle } = useApp();
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(INITIAL_RENDER_COUNT);
  const loadMoreRef = useRef(null);

  const getThumb = (item = {}) => {
    return (
      item?.thumbnail?.md_url ||
      item?.thumbnail?.lg_url ||
      item?.thumbnail?.url ||
      item?.image?.md_url ||
      item?.image?.url ||
      ""
    );
  };

  useEffect(() => {
    setPageTitle("Thư viện sản phẩm");
  }, [setPageTitle]);

  useEffect(() => {
    setVisibleCount(INITIAL_RENDER_COUNT);
  }, [products.length]);

  const visibleProducts = useMemo(() => {
    return products.slice(0, visibleCount);
  }, [products, visibleCount]);

  const hasMore = visibleCount < products.length;

  useEffect(() => {
    if (!hasMore) {
      return;
    }

    const target = loadMoreRef.current;
    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + RENDER_STEP, products.length));
        }
      },
      {
        root: null,
        rootMargin: "320px 0px",
        threshold: 0,
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, products.length]);

  return (
    <Stack gap={4} style={{ paddingBottom: 60 }}>
      {products.length === 0 ? (
        <Center>
          <Text>Chưa có sản phẩm</Text>
        </Center>
      ) : (
        <>
          <SimpleGrid columns={3} columnGap="1" rowGap="1">
            {visibleProducts.map((item) => (
              <Box
                key={item.id}
                borderRadius="sm"
                overflow="hidden"
                cursor="pointer"
                bg="white"
                onClick={() => navigate(`/product/${item.id}`)}
              >
                <AspectRatio ratio={1}>
                  <Image
                    src={getThumb(item)}
                    alt={item.name || "Sản phẩm"}
                    width="100%"
                    height="100%"
                    display="block"
                    objectFit="cover"
                    bg="gray.50"
                    loading="lazy"
                  />
                </AspectRatio>
              </Box>
            ))}
          </SimpleGrid>

          {hasMore && (
            <Center>
              <Box ref={loadMoreRef} height="1px" width="100%" />
            </Center>
          )}
        </>
      )}
    </Stack>
  );
};

export default memo(GalleryPage);
